// Play the computer (plan §9). A bot game runs entirely in the browser: chessops
// owns the rules, chessground the board, a local Fischer clock is authoritative,
// and Stockfish (in a Web Worker) plays the opposite side. Nothing touches the
// server — there's no second human, so no cheating surface — except that the
// finished game is saved to localStorage so it can be reviewed on the analysis
// board.

import { Chessground } from "chessground";
import type { Api as CgApi } from "chessground/api";
import type { Config as CgConfig } from "chessground/config";
import type { Key } from "chessground/types";

import { Chess } from "chessops/chess";
import { makeFen } from "chessops/fen";
import { makeSanAndPlay } from "chessops/san";
import { parseUci } from "chessops/util";

import { h } from "../dom.ts";
import { promotionCard } from "../ui/promotion.ts";
import { navigate } from "../router.ts";
import { checkColor, isPromotion, legalDests, uciSquares } from "../game/board.ts";
import { ClockUI } from "../game/clock.ts";
import { buildReview } from "../game/review.ts";
import { lookupOpening } from "../game/openings.ts";
import { lookupBookPosition } from "../game/openingBook.ts";
import { saveBotGame } from "../game/botHistory.ts";
import type { Color, MoveHist } from "../game/types.ts";
import { Engine } from "../engine/stockfish.ts";
import { chooseOpeningReply, theoryMoveUcis } from "../engine/openingChoice.ts";
import { BOT_LEVELS, botLevel, botLevelLabel, type BotLevel } from "../engine/levels.ts";
import { play } from "../sound.ts";
import { muteButton } from "../ui/mute.ts";
import { confirmDialog } from "../ui/confirm.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { t } from "../i18n/index.ts";

/** Only one bot game lives at a time; tear the previous down on entry. */
let active: { destroy: () => void } | null = null;

const TIME_CONTROLS: { tc: string; label: string; baseMs: number; incMs: number }[] = [
  { tc: "unlimited", label: t("computer.noClock"), baseMs: 0, incMs: 0 },
  { tc: "10+0", label: t("chess.rapid"), baseMs: 600_000, incMs: 0 },
  { tc: "5+0", label: t("chess.blitz"), baseMs: 300_000, incMs: 0 },
  { tc: "5+3", label: t("chess.blitz"), baseMs: 300_000, incMs: 3_000 },
  { tc: "3+2", label: t("chess.blitz"), baseMs: 180_000, incMs: 2_000 },
];

export function computerScreen(): HTMLElement {
  active?.destroy();
  active = null;

  // An Openings-Academy hand-off (plan B3): skip the setup form and start a
  // clockless level-3 game with the course line already on the board.
  const pending = sessionStorage.getItem("chess.pendingOpening");
  if (pending) {
    sessionStorage.removeItem("chess.pendingOpening");
    try {
      const value: unknown = JSON.parse(pending);
      if (!value || typeof value !== "object") throw new Error("invalid opening hand-off");
      const p = value as { color?: unknown; moves?: unknown };
      const color = p.color;
      const moves = p.moves;
      if (
        (color === "white" || color === "black") &&
        Array.isArray(moves) &&
        moves.length > 0 &&
        moves.length <= 64 &&
        moves.every((uci) => typeof uci === "string" && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci))
      ) {
        const gameEl = buildGameLayout(color);
        // The router attaches gameEl after this returns — construct on the next
        // frame so onDisconnect (immediate-run-when-detached, §A9) doesn't fire.
        requestAnimationFrame(() => {
          if (!gameEl.isConnected) return;
          const ctrl = new BotGameCtrl(
            gameEl,
            color,
            botLevel(3),
            TIME_CONTROLS[0], // no clock — this is practice
            moves.map((uci) => ({ uci })),
          );
          active = ctrl;
          onDisconnect(gameEl, () => {
            ctrl.destroy();
            if (active === ctrl) active = null;
          });
        });
        return gameEl;
      }
    } catch {
      // Corrupt payload → fall through to the normal setup form.
    }
  }

  const setup = h(`
    <main class="card wide">
      <header class="topbar">
        <h1 class="brand">${t("computer.title")}</h1>
        <a class="btn ghost" data-link href="/">${t("common.home")}</a>
      </header>

      <form id="bot-setup" class="panel">
        <span class="label">${t("computer.strength")}</span>
        <div class="level-picker" role="radiogroup" aria-label="${t("computer.level")}">
          ${BOT_LEVELS.map(
            (l) => `
            <label class="lvl ${l.level === 3 ? "active" : ""}">
              <input type="radio" name="level" value="${l.level}" ${l.level === 3 ? "checked" : ""} />
              <strong>${l.level}</strong>
            </label>`,
          ).join("")}
        </div>
        <p class="fine level-note">${botLevelLabel(3)}</p>

        <span class="label">${t("computer.timeControl")}</span>
        <div class="tc-picker" role="radiogroup" aria-label="${t("computer.timeControl")}">
          ${TIME_CONTROLS.map(
            (t, i) => `
            <label class="tc ${i === 3 ? "active" : ""}">
              <input type="radio" name="tc" value="${t.tc}" ${i === 3 ? "checked" : ""} />
              <strong>${t.tc === "unlimited" ? "∞" : t.tc}</strong><small>${t.label}</small>
            </label>`,
          ).join("")}
        </div>

        <label class="field">
          <span>${t("computer.playAs")}</span>
          <select name="color">
            <option value="white" selected>${t("chess.white")}</option>
            <option value="black">${t("chess.black")}</option>
            <option value="random">${t("chess.random")}</option>
          </select>
        </label>

        <button class="btn primary" type="submit">${t("computer.startGame")}</button>
      </form>
    </main>
  `);

  // Level picker: reflect selection + update the note.
  const levelPicker = setup.querySelector(".level-picker")!;
  const levelNote = setup.querySelector<HTMLElement>(".level-note")!;
  levelPicker.addEventListener("change", () => {
    let chosen = 3;
    levelPicker.querySelectorAll(".lvl").forEach((label) => {
      const input = label.querySelector("input") as HTMLInputElement;
      label.classList.toggle("active", input.checked);
      if (input.checked) chosen = Number(input.value);
    });
    levelNote.textContent = botLevelLabel(chosen);
  });

  // Time-control picker highlight.
  const tcPicker = setup.querySelector(".tc-picker")!;
  tcPicker.addEventListener("change", () => {
    tcPicker.querySelectorAll(".tc").forEach((label) => {
      const input = label.querySelector("input") as HTMLInputElement;
      label.classList.toggle("active", input.checked);
    });
  });

  const form = setup.querySelector<HTMLFormElement>("#bot-setup")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const level = botLevel(Number(data.get("level") ?? 3));
    const tcVal = String(data.get("tc") ?? "5+3");
    const tc = TIME_CONTROLS.find((t) => t.tc === tcVal) ?? TIME_CONTROLS[3];
    let color = String(data.get("color") ?? "white");
    if (color === "random") color = Math.random() < 0.5 ? "white" : "black";

    const gameEl = buildGameLayout(color as Color);
    setup.replaceWith(gameEl);
    const ctrl = new BotGameCtrl(gameEl, color as Color, level, tc);
    active = ctrl;
    onDisconnect(gameEl, () => {
      ctrl.destroy();
      if (active === ctrl) active = null;
    });
  });

  return setup;
}

// ---------------------------------------------------------------------------
// Bot game controller
// ---------------------------------------------------------------------------

interface Tc {
  tc: string;
  baseMs: number;
  incMs: number;
}

const MIN_THINK_MS = 350; // floor on the bot's visible think time, for feel

class BotGameCtrl {
  private cg: CgApi;
  private clockUI?: ClockUI;
  private pos = Chess.default();
  private moves: MoveHist[] = [];
  private status = "playing";
  private botColor: Color;
  private engine = new Engine();
  private engineReady = false;
  private openingLabel = "";
  private readonly openingPractice: boolean;

  // Local clock authority (ms). Null when playing without a clock.
  private clock: { wMs: number; bMs: number; turn: Color; startedAt: number } | null = null;
  private flagTimer?: number;
  private baseMs = 0;
  private incMs = 0;

  // Cancels an in-flight bot move if the game ends or we tear down.
  private gen = 0;

  constructor(
    private root: HTMLElement,
    private humanColor: Color,
    private level: BotLevel,
    tc: Tc,
    /** Moves replayed onto the board before play starts (Openings hand-off). */
    preMoves?: { uci: string }[],
  ) {
    this.botColor = humanColor === "white" ? "black" : "white";
    this.openingPractice = Boolean(preMoves?.length);

    const boardEl = root.querySelector<HTMLElement>(".cg-board-wrap")!;
    this.cg = Chessground(boardEl, this.baseConfig());

    if (tc.baseMs > 0) {
      this.clock = { wMs: tc.baseMs, bMs: tc.baseMs, turn: "white", startedAt: performance.now() };
      this.clockUI = new ClockUI(
        {
          white: root.querySelector<HTMLElement>('[data-clock="white"]')!,
          black: root.querySelector<HTMLElement>('[data-clock="black"]')!,
        },
        (color) => {
          if (color === this.humanColor) play("low");
        },
      );
    } else {
      root.querySelectorAll<HTMLElement>(".clock").forEach((c) => (c.textContent = "∞"));
    }
    this.baseMs = tc.baseMs;
    this.incMs = tc.incMs;

    // A Study hand-off is just legal move replay, so paint it immediately. The
    // expensive engine download/handshake must not hold the board at move one.
    if (preMoves?.length) this.replayOpening(preMoves);
    this.syncBoard();
    this.wireControls();
    this.setPlayableControls(false);
    this.setNote(t(this.openingPractice ? "computer.positionReadyLoadingEngine" : "computer.loadingEngine"));
    void this.boot();
  }

  private destroyed = false;

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.gen++;
    this.engine.destroy();
    this.clockUI?.destroy();
    this.cg.destroy();
    if (this.flagTimer) clearInterval(this.flagTimer);
  }

  // --- engine start ---------------------------------------------------

  private async boot(): Promise<void> {
    const gen = this.gen;
    try {
      await this.engine.init();
      await this.engine.newGame();
      await this.engine.setOption("Skill Level", this.level.skill);
      if (this.openingPractice) await this.engine.setOption("MultiPV", 5);
    } catch {
      if (gen === this.gen) {
        this.setNote(t("computer.engineFailed"));
        this.setPlayableControls(false, true);
      }
      return;
    }
    if (gen !== this.gen) return; // torn down while loading
    this.engineReady = true;
    this.setNote(this.openingLabel);
    this.setPlayableControls(true);
    // The clock only starts once the game is actually playable — don't charge
    // anyone for the (potentially multi-second) engine/wasm load.
    if (this.clock) this.clock.startedAt = performance.now();
    this.syncBoard();
    this.startFlagWatch();
    if (this.pos.turn === this.botColor) this.scheduleBotMove();
  }

  /** Replay and display a Study line without waiting for Stockfish. */
  private replayOpening(preMoves: readonly { uci: string }[]): void {
    for (const m of preMoves) {
      const move = parseUci(m.uci);
      if (!move || !this.pos.isLegal(move)) break;
      const san = makeSanAndPlay(this.pos, move);
      this.moves.push({ uci: m.uci, san });
    }
    this.appendMoveList();
    const gen = this.gen;
    void lookupOpening(buildReview(this.moves)).then((hit) => {
      if (!hit || gen !== this.gen || this.status !== "playing") return;
      this.openingLabel = `${hit.eco} · ${hit.name}`;
      if (this.engineReady) this.setNote(this.openingLabel);
    });
  }

  // --- board ----------------------------------------------------------

  private baseConfig(): CgConfig {
    return {
      orientation: this.humanColor,
      turnColor: "white",
      movable: { free: false, color: undefined, dests: new Map(), showDests: true },
      premovable: { enabled: true },
      draggable: { showGhost: true },
      highlight: { lastMove: true, check: true },
      animation: { enabled: true, duration: 180 },
    };
  }

  private syncBoard(): void {
    const ongoing = this.status === "playing" && this.engineReady;
    const humanTurn = ongoing && this.pos.turn === this.humanColor;
    const lastMove =
      this.moves.length > 0 ? uciSquares(this.moves[this.moves.length - 1].uci) : undefined;

    this.cg.set({
      fen: makeFen(this.pos.toSetup()),
      turnColor: this.pos.turn as Color,
      lastMove,
      check: checkColor(this.pos),
      movable: {
        color: ongoing ? this.humanColor : undefined,
        dests: humanTurn ? legalDests(this.pos) : new Map(),
        events: { after: (orig, dest) => this.onUserMove(orig, dest) },
      },
      premovable: { enabled: ongoing },
    });
    if (humanTurn) this.cg.playPremove();
  }

  private onUserMove(orig: Key, dest: Key): void {
    if (this.status !== "playing" || this.pos.turn !== this.humanColor) return;
    if (isPromotion(this.pos, orig, dest)) {
      void this.askPromotion().then((role) => {
        if (!role) {
          this.syncBoard(); // cancelled → restore
          return;
        }
        this.applyMove(orig + dest + role, this.humanColor);
      });
    } else {
      this.applyMove(orig + dest, this.humanColor);
    }
  }

  // --- move application (shared by human + bot) -----------------------

  private applyMove(uci: string, mover: Color): void {
    const move = parseUci(uci);
    if (!move || !this.pos.isLegal(move)) return;

    if (this.clock && !this.stepClock(mover)) {
      // stepClock ended the game on a flag.
      return;
    }

    const san = makeSanAndPlay(this.pos, move);
    this.moves.push({ uci, san });
    this.appendMoveList();

    if (this.clock) {
      this.clock.turn = this.pos.turn as Color;
      this.clock.startedAt = performance.now();
      this.syncClockUI();
    }

    const outcome = this.detectEnd();
    if (outcome) {
      // Render the game-ending move before the result overlay. makeSanAndPlay
      // above advanced `this.pos` + the move list, but the board is only ever
      // updated by an explicit cg.set and endGame merely locks it — so without
      // this a mating (or otherwise final) move never appears and the board
      // freezes on the prior position. Passing `fen` makes chessground animate.
      this.cg.set({
        fen: makeFen(this.pos.toSetup()),
        turnColor: this.pos.turn as Color,
        lastMove: uciSquares(uci),
        check: checkColor(this.pos),
      });
      this.endGame(outcome.status, outcome.winner);
      return;
    }

    // Move feedback: check → check, capture ('x') → capture, else plain move.
    if (san.includes("+")) play("check");
    else if (san.includes("x")) play("capture");
    else play("move");

    this.syncBoard();
    if (this.pos.turn === this.botColor) this.scheduleBotMove();
  }

  /**
   * Deduct the mover's elapsed time and apply the increment. Returns false if
   * the mover flagged (game already ended inside). Only called with a clock.
   */
  private stepClock(mover: Color): boolean {
    const c = this.clock!;
    const now = performance.now();
    const elapsed = now - c.startedAt;
    const remainingKey = mover === "white" ? "wMs" : "bMs";
    let remaining = c[remainingKey] - elapsed;
    if (remaining < 0) {
      c[remainingKey] = 0;
      this.flag(mover);
      return false;
    }
    remaining += this.incMs;
    c[remainingKey] = remaining;
    return true;
  }

  private scheduleBotMove(): void {
    const gen = this.gen;
    const fen = makeFen(this.pos.toSetup());
    const started = performance.now();
    const book = this.openingPractice ? lookupBookPosition(fen) : Promise.resolve(null);
    void Promise.all([
      this.engine.go({ fen, depth: this.level.depth, movetimeMs: this.level.movetimeMs }),
      book,
    ])
      .then(([res, position]) => {
        if (gen !== this.gen || this.status !== "playing") return;
        let move = res.bestmove;
        // In opening practice, vary only among continuations recognised by the
        // local theory graph *and* ranked in Stockfish's top five. The graph
        // supplies the opening boundary; engine rank supplies chess quality.
        if (position && position.moves.length > 1) {
          const theory = theoryMoveUcis(position.moves);
          move = chooseOpeningReply(res.infos ?? [], theory) ?? move;
        }
        if (!move) return; // no legal move: detectEnd already handled it
        const wait = Math.max(0, MIN_THINK_MS - (performance.now() - started));
        window.setTimeout(() => {
          if (gen === this.gen && this.status === "playing") this.applyMove(move!, this.botColor);
        }, wait);
      })
      .catch(() => undefined);
  }

  // --- end detection --------------------------------------------------

  private repetition = new Map<string, number>();

  private detectEnd(): { status: string; winner: Color | null } | null {
    if (this.pos.isCheckmate()) {
      // The side to move is mated → the mover won.
      return { status: "mate", winner: this.pos.turn === "white" ? "black" : "white" };
    }
    if (this.pos.isStalemate()) return { status: "stalemate", winner: null };
    if (this.pos.isInsufficientMaterial()) return { status: "insufficient", winner: null };
    if (this.pos.halfmoves >= 100) return { status: "fifty", winner: null };

    const epd = makeFen(this.pos.toSetup()).split(" ").slice(0, 4).join(" ");
    const seen = (this.repetition.get(epd) ?? 0) + 1;
    this.repetition.set(epd, seen);
    if (seen >= 3) return { status: "threefold", winner: null };

    return null;
  }

  /** A side ran out of time. Asymmetric rule: no mating material → draw. */
  private flag(flagged: Color): void {
    const winnerColor = flagged === "white" ? "black" : "white";
    const winnerHasMaterial = !this.pos.hasInsufficientMaterial(winnerColor);
    if (winnerHasMaterial) this.endGame("timeout", winnerColor);
    else this.endGame("insufficient", null); // flag vs bare king → draw
  }

  private endGame(status: string, winner: Color | null): void {
    if (this.status !== "playing") return;
    this.status = status;
    this.gen++; // cancel any pending bot move
    if (this.flagTimer) {
      clearInterval(this.flagTimer);
      this.flagTimer = undefined;
    }
    this.clockUI?.stop();
    this.cg.set({ movable: { color: undefined, dests: new Map() }, premovable: { enabled: false } });
    play("end");

    const result: "won" | "lost" | "draw" =
      winner === null ? "draw" : winner === this.humanColor ? "won" : "lost";
    const id = saveBotGame({
      level: this.level.level,
      humanColor: this.humanColor,
      tc: this.clock ? this.tcString() : "unlimited",
      status,
      winner,
      result,
      moves: this.moves,
    });

    this.showResult(status, winner, id);
  }

  // --- clock display + flag watcher -----------------------------------

  private startFlagWatch(): void {
    if (!this.clock) return;
    this.syncClockUI();
    this.flagTimer = window.setInterval(() => {
      if (this.status !== "playing" || !this.clock) return;
      const c = this.clock;
      const elapsed = performance.now() - c.startedAt;
      const remaining = (c.turn === "white" ? c.wMs : c.bMs) - elapsed;
      if (remaining <= 0) this.flag(c.turn);
    }, 200);
  }

  private syncClockUI(): void {
    if (!this.clock || !this.clockUI) return;
    const c = this.clock;
    this.clockUI.sync(
      { white: c.wMs / 1000, black: c.bMs / 1000, lag: 0 },
      c.turn,
      this.status === "playing",
    );
  }

  private tcString(): string {
    return `${Math.round(this.baseMs / 60000)}+${Math.round(this.incMs / 1000)}`;
  }

  // --- move list ------------------------------------------------------

  private appendMoveList(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.replaceChildren();
    for (let i = 0; i < this.moves.length; i += 2) {
      const row = document.createElement("div");
      row.className = "move-row";
      row.innerHTML =
        `<span class="mn">${i / 2 + 1}.</span>` +
        `<span class="san">${this.moves[i].san}</span>` +
        `<span class="san">${this.moves[i + 1] ? this.moves[i + 1].san : ""}</span>`;
      list.append(row);
    }
    list.scrollTop = list.scrollHeight;
  }

  // --- controls + result UI -------------------------------------------

  private wireControls(): void {
    this.root.querySelector<HTMLElement>(".mute-slot")?.append(muteButton());
    this.root.querySelector<HTMLButtonElement>('[data-act="resign"]')?.addEventListener("click", async () => {
      if (this.status !== "playing") return;
      if (await confirmDialog(t("computer.resignConfirm"), { danger: true })) {
        if (this.status === "playing") this.endGame("resign", this.botColor);
      }
    });
    this.root.querySelector<HTMLButtonElement>('[data-act="new"]')?.addEventListener("click", () => {
      navigate("/play/computer");
    });
  }

  /** Lock game-changing controls until the imported position is playable. */
  private setPlayableControls(ready: boolean, failed = false): void {
    const newGame = this.root.querySelector<HTMLButtonElement>('[data-act="new"]');
    const resign = this.root.querySelector<HTMLButtonElement>('[data-act="resign"]');
    if (newGame) newGame.disabled = !ready && !failed;
    if (resign) resign.disabled = !ready;
  }

  private showResult(status: string, winner: Color | null, gameId: string): void {
    const box = this.root.querySelector<HTMLElement>(".result")!;
    box.hidden = false;
    box.replaceChildren();
    const p = document.createElement("p");
    p.className = "result-line";
    p.textContent = resultText(status, winner, this.humanColor);
    box.append(p);

    const analyze = mkBtn(t("game.analyze"), "primary");
    analyze.onclick = () => navigate(`/analysis/bot/${gameId}`);
    const again = mkBtn(t("computer.newGame"), "ghost");
    again.onclick = () => navigate("/play/computer");
    const home = mkBtn(t("common.home"), "ghost");
    home.onclick = () => navigate("/");
    box.append(analyze, again, home);
  }

  private askPromotion(): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = this.root.querySelector<HTMLElement>(".promotion")!;
      // Guarded by `this.pos.turn === this.humanColor` at the call site, so the
      // side to move is the human who's promoting — render in that colour.
      const card = promotionCard(this.pos.turn as Color, (code) => {
        overlay.hidden = true;
        resolve(code);
      });
      overlay.replaceChildren(card);
      overlay.hidden = false;
    });
  }

  private setNote(text: string): void {
    const el = this.root.querySelector<HTMLElement>(".note");
    if (el) el.textContent = text;
  }
}

// ---------------------------------------------------------------------------
// Layout + helpers
// ---------------------------------------------------------------------------

function buildGameLayout(humanColor: Color): HTMLElement {
  const topColor: Color = humanColor === "white" ? "black" : "white";
  const bottomColor: Color = humanColor;
  return h(`
    <main class="game">
      <div class="board-col">
        <div class="cg-board-wrap"></div>
        <div class="promotion" hidden></div>
      </div>
      <aside class="side">
        <div class="side-top">
          <span class="conn ok">${t("chess.computer")}</span>
          <a class="btn ghost sm" data-link href="/">${t("common.home")}</a>
          <span class="mute-slot"></span>
        </div>

        <div class="player">
          <span class="pname">${t("chess.computer")}</span>
          <span class="clock" data-clock="${topColor}">--</span>
        </div>

        <div class="movelist"></div>

        <div class="player">
          <span class="pname">${t("computer.you")}</span>
          <span class="clock" data-clock="${bottomColor}">--</span>
        </div>

        <p class="note" role="status" aria-live="polite"></p>

        <div class="controls">
          <button class="btn ghost" data-act="new" type="button">${t("computer.newGame")}</button>
          <button class="btn ghost" data-act="resign" type="button">${t("computer.resign")}</button>
        </div>

        <div class="result" hidden></div>
      </aside>
    </main>
  `);
}

function resultText(status: string, winner: Color | null, myColor: Color): string {
  const reason: Record<string, string> = {
    mate: t("term.checkmate"),
    resign: t("term.resignation"),
    timeout: t("term.timeout"),
    stalemate: t("term.stalemate"),
    insufficient: t("term.insufficient"),
    threefold: t("term.repetition"),
    fifty: t("term.fiftyMove"),
    draw: t("term.draw"),
  };
  const detail = reason[status] ?? status;
  if (winner === null) return `${t("result.drawWord")} · ${detail}`;
  return `${winner === myColor ? t("result.youWon") : t("result.youLost")} · ${detail}`;
}

function mkBtn(text: string, kind: "primary" | "ghost"): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = `btn ${kind}`;
  b.type = "button";
  b.textContent = text;
  return b;
}
